import { createContext, useContext } from 'react';
import {
  componentDatasetKey,
  componentTextDatasetKey,
  type ReportValueFormat,
} from '@erp/shared-types';

// ── Datasets ──────────────────────────────────────────────────────────────────
// The canvas is data-source-agnostic: a surface (query builder, AR summary, …)
// adapts whatever it fetches into one or more named tabular datasets, and each
// chart component binds to columns of the dataset named by its `datasetKey`
// (undefined = the surface's default dataset).

export type ReportDatasetValue = string | number | boolean | null;
export type ReportDatasetRow = Record<string, ReportDatasetValue>;

export interface ReportDatasetColumn {
  key: string;
  label: string;
  /** 'number' columns are offered for value axes; everything else is categorical. */
  dataType: string;
  /**
   * How the column's values read in text, KPIs and Tables (money, a share,
   * days, a date), as the surface's catalog defines it. Absent = as written.
   */
  format?: ReportValueFormat;
  /** What the column holds, from the surface's catalog. */
  description?: string;
}

/**
 * Why a dataset has no rows to show: its query was refused (`code` names
 * why, e.g. `not_in_catalog`, and `path` where in the query) or its data
 * didn't load.
 */
export interface ReportDatasetError {
  code?: string;
  message: string;
  path?: readonly (string | number)[];
}

export interface ReportDataset {
  key: string;
  /** Shown in the dataset picker when a surface provides multiple datasets. */
  label: string;
  columns: ReportDatasetColumn[];
  rows: ReportDatasetRow[];
  /**
   * The rows before Report filters, where they differ from `rows`: a
   * category's color is its place among these, so a filter never recolors.
   */
  allRows?: ReportDatasetRow[];
  isLoading?: boolean;
  /**
   * The rows are already the reading component's Server aggregation (one per
   * group, or one for a whole-table aggregate), not the dataset's own rows.
   */
  aggregated?: boolean;
  /** Set instead of rows when the dataset couldn't be answered. */
  error?: ReportDatasetError;
  /**
   * One row of totals for the whole dataset, when the surface has one (a
   * query with no dimensions): what `{placeholders}` in text read.
   */
  totals?: ReportDatasetRow;
  /** Rows in the whole source, before a limit cut `rows` short. */
  totalRows?: number;
}

export interface ReportDataContextValue {
  datasets: ReportDataset[];
  defaultDatasetKey: string;
  /** Surface-specific hint shown in empty charts before data exists. */
  emptyHint?: string;
}

export const ReportDataContext = createContext<ReportDataContextValue>({
  datasets: [],
  defaultDatasetKey: 'default',
});

export function useReportDatasets(): ReportDataContextValue {
  return useContext(ReportDataContext);
}

/**
 * Dataset a component should read: the one named by its datasetKey, else the
 * surface default. Null when the dataset doesn't exist (yet) — charts render
 * their empty state.
 */
export function useReportDataset(
  datasetKey: string | undefined,
): ReportDataset | null {
  const { datasets, defaultDatasetKey } = useContext(ReportDataContext);
  const key = datasetKey ?? defaultDatasetKey;
  return datasets.find((d) => d.key === key) ?? null;
}

/** The dataset a component reads: its `datasetKey`'s, or its own source's. */
export function useComponentDataset(component: {
  id: string;
  datasetKey?: string;
  source?: unknown;
}): ReportDataset | null {
  return useReportDataset(componentDatasetKey(component));
}

/** The one-row dataset a component's `textSource` is published as; null without one. */
export function useComponentTextDataset(component: {
  id: string;
  datasetKey?: string;
  textSource?: unknown;
}): ReportDataset | null {
  const { datasets } = useContext(ReportDataContext);
  const key = componentTextDatasetKey(component);
  return key ? (datasets.find((d) => d.key === key) ?? null) : null;
}
